import React from "react";
import {
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    Typography,
    Box
} from "@mui/material";
import DialogComponent from "components/DialogComponent";
import { Form, Formik } from "formik";
import { useUpdateProjectSettingsMutation } from "store/slices/apis/projectSettingsSlice";
import { formatKeysToLabel } from "utils/reusable";
import { useDispatch } from "react-redux";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";

const keysNotToShow = ["projectId", "updatedBy", "updatedDate"]

export default function UpdateProjectSettings({ data, open, onClose }) {
    const dispatch = useDispatch();
    const initialValues = data?.data || {};

    const [updateFn, { isLoading }] = useUpdateProjectSettingsMutation()

    const handleSubmit = async (values, { setSubmitting }) => {
        // console.log("Values", values)

        try {
            const result = await updateFn(values).unwrap();
            if (result.status === "Success") {
                openNotification("Project Settings Updated Successfully", "success");
                onClose();
            }
        } catch (error) {
            console.error("Update failed", error);
            openNotification("Failed to Updated Project Settings");
        } finally {
            setSubmitting(false)
        }
    };

    const openNotification = (msg, type = "error", duration = 5000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };


    const renderField = (key, value, handleChange, values, setFieldValue) => {

        const type = typeof value;
        if (type === "boolean") {
            return (
                <FormControlLabel
                    control={
                        <Switch
                            checked={values[key]}
                            onChange={(e) => setFieldValue(key, e.target.checked)}
                            size="small"
                        />
                    }
                    label={values[key] ? "Enabled" : "Disabled"}
                    sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 12, color: values[key] ? "#16A36A" : "#64748B", fontWeight: 600 } }}
                />
            );
        }


        if (type === "number") {
            return (
                <TextField
                    fullWidth
                    name={key}
                    type="number"
                    value={values[key] ?? ""}
                    onChange={handleChange}
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { minHeight: 36, borderRadius: "3px", fontSize: 13 } }}
                />
            );
        }


        return (
            <TextField
                fullWidth
                name={key}
                value={values[key] ?? ""}
                onChange={handleChange}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { minHeight: 36, borderRadius: "3px", fontSize: 13 } }}
            />
        );
    };

    return (
        <DialogComponent title="Update Project Settings" open={open} handleClose={onClose} maxWidth="md">
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={handleSubmit}
            >
                {({ values, handleChange, setFieldValue, isSubmitting }) => (
                    <Form>
                        <Box sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontSize: 12, lineHeight: "17px", color: "#64748B" }}>Update project-level defaults and controls. Changes apply immediately after saving.</Typography>
                        </Box>
                        <Grid container spacing={1.5}>
                        {Object.entries(values).map(([key, value]) => {
                            if (keysNotToShow?.includes(key)) return null
                            return <Grid size={{ xs: 12, md: 6 }} sx={{ p: 1.25, border: "1px solid #E2E8F0", borderRadius: "3px", bgcolor: "#FFFFFF" }} key={key}>
                                    <Typography sx={{ mb: 0.75, fontSize: 12, lineHeight: "16px", fontWeight: 600, color: "#475569" }}>
                                        {formatKeysToLabel(key)}
                                    </Typography>
                                    {renderField(
                                        key,
                                        value,
                                        handleChange,
                                        values,
                                        setFieldValue
                                    )}
                            </Grid>
                        })}
                        </Grid>
                        <DialogActions sx={{ mt: 2, px: 0, pt: 1.5, borderTop: "1px solid #E2E8F0" }}>
                            <Button onClick={onClose}
                                sx={{
                                    textTransform: "none",
                                    color: "#475569",
                                    fontSize: "13px",
                                    lineHeight: "18px",
                                    fontFamily: "Outfit, sans-serif",
                                    borderRadius: "3px",
                                    fontWeight: 500,
                                    px: 2,
                                    py: 1,
                                }}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                loading={isLoading}
                                sx={{
                                    backgroundColor: "#2563EB",
                                    borderRadius: "3px",
                                    textTransform: "none",
                                    fontWeight: 500,
                                    fontSize: "13px",
                                    lineHeight: "18px",
                                    fontFamily: "Outfit, sans-serif",
                                    color: "#FFFFFF",
                                    px: 2.25,
                                    py: 1,
                                }}
                                disabled={isLoading}
                            >
                                Save
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>

        </DialogComponent>
    );
}
